/** @jsx jsx */
import { type MutableRefObject, memo, type ReactNode, useState, forwardRef, type Ref } from 'react';
import { css, keyframes, jsx, cssMap } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { Popup, type PopupComponentProps } from '@atlaskit/popup';
import type { EntryPointActions } from '@atlassian/jira-entry-point/src/controllers/utils/types.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { useCellHoverCallbacks } from '../controllers/index.tsx';
import { PACKAGE_NAME, TEAM_NAME } from '../constants.tsx';

type MinimumWidth = 'narrow';

export type Props = {
	content?: ReactNode;
	children: ReactNode | ((props: { ref: Ref<HTMLElement> }) => ReactNode);
	contentRef?: MutableRefObject<HTMLElement | null>;
	minimumWidth?: MinimumWidth;
	entryPointActions?: EntryPointActions;
	isInteractive?: boolean;
	shouldGetParentRef?: boolean;
};

export const HoverPopover = memo(
	({
		content,
		children,
		contentRef,
		minimumWidth,
		entryPointActions,
		isInteractive,
		shouldGetParentRef,
	}: Props) => {
		const [isPopupOpen, setIsPopupOpen] = useState(false);

		useCellHoverCallbacks({
			contentRef,
			onPreload: entryPointActions?.preload,
			onLoad: entryPointActions?.load,
			onPopupToggle: setIsPopupOpen,
			shouldGetParentRef,
		});

		if (content) {
			return (
				<UFOSegment name="jira-list-hover-popover">
					<JSErrorBoundary
						id="jira-list-hover-popover"
						packageName={PACKAGE_NAME}
						teamName={TEAM_NAME}
						fallback="flag"
					>
						<Popup
							content={() => (
								<div
									data-testid="hover-popover.ui.hover-popover-content"
									aria-hidden
									css={[popoverStyles, containerVariantStyles[minimumWidth ?? 'content']]}
								>
									{content}
								</div>
							)}
							popupComponent={forwardRef<HTMLDivElement, PopupComponentProps>(
								({ children: popupChildren, ...restProps }, ref) => (
									<div
										css={[containerStyles, isInteractive && interactiveStyles]}
										{...restProps}
										ref={ref}
									>
										{popupChildren}
									</div>
								),
							)}
							// eslint-disable-next-line jsx-a11y/no-autofocus
							autoFocus={false}
							isOpen={isPopupOpen}
							trigger={({ ref }) => {
								return typeof children === 'function' ? (
									children({ ref })
								) : (
									<div css={triggerContainer} ref={ref}>
										{children}
									</div>
								);
							}}
							placement="bottom-start"
						/>
					</JSErrorBoundary>
				</UFOSegment>
			);
		}

		return typeof children === 'function' ? children({ ref: null }) : children;
	},
);

const fadeIn = keyframes({
	from: {
		opacity: '0',
	},
	to: {
		opacity: '100',
	},
});

const triggerContainer = css({ width: '100%' });

const interactiveStyles = css({
	marginBlock: token('space.negative.100'),
	paddingBlock: token('space.150'),
	pointerEvents: 'auto',
});

const containerStyles = css({
	marginBlock: token('space.100'),
	animationName: `${fadeIn}`,
	opacity: '0',
	animationDuration: '0.1s',
	animationTimingFunction: 'ease-in',
	animationFillMode: 'forwards',
	maxWidth: '300px',
	pointerEvents: 'none',
});

const containerVariantStyles = cssMap({
	narrow: {},
	content: {},
});

const popoverStyles = css({
	backgroundColor: token('elevation.surface.overlay'),
	paddingInline: token('space.150'),
	paddingBlock: token('space.075'),
	borderRadius: token('border.radius'),
	boxShadow: token('elevation.shadow.overlay'),
	fontBody: token('font.body'),
	fontWeight: token('font.weight.regular'),
	overflowWrap: 'break-word',
	maxHeight: '300px',
	overflowY: 'scroll',
	wordBreak: 'break-word',
});
